// Implements: F-UIX-017, F-UIX-007
/**
 * The Ctrl+K command palette of the redesign mockup (UX-03): a modal dialog whose search field is a
 * WAI-ARIA combobox over a listbox of commands, grouped ("Ir a", "Preferencias", "Cuenta").
 *
 * The shell builds the commands from the same permission model as the sidebar, so the palette
 * offers nothing the person cannot open. The focus stays in the field (`aria-activedescendant`); the
 * arrows move with wrap, Enter or a press runs the active command and closes, Escape closes
 * (`useModalFocus`, topmost layer only) and the focus goes back to whatever opened it.
 */

import { useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactElement } from "react";
import { createPortal } from "react-dom";

import { admits } from "../../modules/backoffice/model/sections";
import type { BackofficeAudience } from "../../modules/backoffice/model/sections";
import type { IconName } from "../../shared/design-system/icons";
import { useTranslation } from "../../shared/i18n";
import type { TranslationKey } from "../../shared/i18n";
import type { BackofficeScope } from "../../types/api";
import { Icon } from "../../shared/ui/atoms/Icon";
import { Kbd } from "../../shared/ui/atoms/Kbd";
import { normaliseSearch } from "../../shared/ui/molecules/Combobox";
import { useModalFocus } from "../../shared/ui/useModalFocus";
import styles from "./CommandPalette.module.css";

export interface PaletteCommand {
  readonly id: string;
  /** The heading the command is listed under (from `t()`). */
  readonly groupLabel: string;
  readonly label: string;
  readonly icon: IconName;
  /** The key that does the same from anywhere, shown on the right. */
  readonly shortcut?: string | undefined;
  /** Already matched by whoever supplied it (a server search): listed whatever the label says. */
  readonly matched?: boolean | undefined;
  readonly run: () => void;
}

/**
 * An action of the palette that only some people are offered: its audience is read from the
 * server's permission answer, as the sidebar's is (never a role name). The shell lists the ones
 * `offeredActions` returns under "Acciones".
 */
export interface PaletteAction {
  readonly id: string;
  readonly labelKey: Exclude<Extract<TranslationKey, `app.palette.${string}`>, "app.palette.empty">;
  readonly icon: IconName;
  readonly href: string;
  readonly audience: BackofficeAudience;
}

export const PALETTE_ACTIONS: readonly PaletteAction[] = [
  // Mockup v3 P3: invite people straight into the invitations page's dialog.
  {
    id: "invite-people",
    labelKey: "app.palette.invitePeople",
    icon: "user-plus",
    href: "/backoffice/invitations?invite=1",
    audience: ["invitation:issue"],
  },
];

/** The actions a person holding `scopes` is offered, in palette order. */
export function offeredActions(scopes: readonly BackofficeScope[]): readonly PaletteAction[] {
  return PALETTE_ACTIONS.filter((action) => admits(action.audience, scopes));
}

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly commands: readonly PaletteCommand[];
  readonly onClose: () => void;
  /** Called with what is typed, so the owner can add results of its own (the mockup search). */
  readonly onQuery?: ((query: string) => void) | undefined;
}

export function CommandPalette({ open, commands, onClose, onQuery }: CommandPaletteProps): ReactElement | null {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  useModalFocus(open, dialogRef, onClose);

  if (!open) {
    // Closed: the next opening starts clean (verifier minor 5), adjusted during render, not in an effect.
    if (query !== "" || active !== 0) {
      setQuery("");
      setActive(0);
    }
    return null;
  }

  const needle = normaliseSearch(query);
  const visible = commands.filter(
    (command) => command.matched === true || normaliseSearch(`${command.label} ${command.groupLabel}`).includes(needle),
  );
  const current = Math.min(active, Math.max(visible.length - 1, 0));
  const optionId = (index: number): string => `${listId}-${String(index)}`;

  const runAt = (index: number): void => {
    const command = visible[index];
    if (command === undefined) return;
    onClose();
    command.run();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const count = visible.length;
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && count > 0) {
      event.preventDefault();
      setActive((current + (event.key === "ArrowDown" ? 1 : -1) + count) % count);
    } else if (event.key === "Enter") {
      event.preventDefault();
      runAt(current);
    }
  };

  // A heading before the first command of each group.
  const headed = visible.map((command, index) => ({
    command,
    heading: index === 0 || visible[index - 1]?.groupLabel !== command.groupLabel ? command.groupLabel : null,
  }));
  return createPortal(
    <div
      className={styles.scrim}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={dialogRef} className={styles.palette} role="dialog" aria-modal="true" aria-label={t("app.palette.label")}>
        <div className={styles.inputRow}>
          <Icon name="search" />
          <input
            className={styles.input}
            type="text"
            role="combobox"
            aria-label={t("app.palette.placeholder")}
            placeholder={t("app.palette.placeholder")}
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={visible.length === 0 ? undefined : optionId(current)}
            autoComplete="off"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
              onQuery?.(event.target.value);
            }}
            onKeyDown={onKeyDown}
          />
          <Kbd>{t("app.kbd.esc")}</Kbd>
        </div>
        <div id={listId} className={styles.list} role="listbox" aria-label={t("app.palette.results")}>
          {headed.map(({ command, heading }, index) => {


            return [
              heading === null ? null : (
                <div key={`group-${heading}`} className={styles.group} role="presentation">
                  {heading}
                </div>
              ),
              <div
                key={command.id}
                id={optionId(index)}
                className={styles.option}
                role="option"
                aria-selected={index === current}
                tabIndex={-1}
                onMouseDown={(event) => {
                  event.preventDefault();
                  runAt(index);
                }}
              >
                <Icon name={command.icon} />
                <span className={styles.optionLabel}>{command.label}</span>
                {command.shortcut === undefined ? null : <Kbd>{command.shortcut}</Kbd>}
              </div>,
            ];
          })}
        </div>
        <div className={styles.empty} role="status">
          {visible.length === 0 ? t("app.palette.empty", { query }) : ""}
        </div>
        <div className={styles.foot} aria-hidden="true">
          <span>
            <Kbd>{t("app.kbd.up")}</Kbd> <Kbd>{t("app.kbd.down")}</Kbd> {t("app.palette.move")}
          </span>
          <span>
            <Kbd>{t("app.kbd.enter")}</Kbd> {t("app.palette.open")}
          </span>
          <span>
            <Kbd>{t("app.kbd.esc")}</Kbd> {t("app.palette.close")}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
